import { useEffect, useState } from "react";
import { useApiClient, useSafeApiCall } from "src/hooks/api";
import { API_PREFIX_PATH } from "src/constants";
import { useDealer } from "src/hooks/useDealer";
import type { Employee } from "src/types/employee";

interface GetEmployeesResponseDTO {
  items: Employee[];
  total: number;
}

/**
 * Hook to fetch and store all employees for the current dealer in an array
 * This is useful for dropdowns and other components that need access to the full employee list
 * @param enabled - Whether to fetch employees. Defaults to true. Set to false for lazy loading.
 */
export const useDealerEmployees = (enabled: boolean = true) => {
  const { dealer_id } = useDealer();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const safeApiCall = useSafeApiCall();
  const { fetchFromApi } = useApiClient();

  useEffect(() => {
    if (!enabled) {
      setEmployees([]);
      setError(null);
      return;
    }

    if (!dealer_id) return;

    setIsLoading(true);
    setError(null);

    const path = `${API_PREFIX_PATH}/${dealer_id}/employees`;

    // Fetch all employees - use a reasonable page size like EmployeeTable does
    safeApiCall(
      () =>
        fetchFromApi(path, {
          queryParams: {
            limit: 100,
            offset: 0,
          },
        }) as Promise<GetEmployeesResponseDTO>,
    )
      .then((data) => {
        setEmployees(data?.items || []);
      })
      .catch((err) => {
        console.error("Failed to fetch employees:", err);
        setError(err as Error);
        setEmployees([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [dealer_id, enabled]);

  return { employees, isLoading, error };
};
